import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
// @mui
import {
  Box,
  Stack,
  Button,
  Drawer,
  Divider,
  IconButton,
  Typography,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
// components
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';

import { useFetcher } from 'src/api/fetcher';
import { JobService } from 'src/api';
import IndustriesList from './IndustriesList';

// ----------------------------------------------------------------------

IndustriesSidebar.propTypes = {
  openSidebar: PropTypes.bool,
  onOpenSidebar: PropTypes.func,
  onCloseSidebar: PropTypes.func,
};

export default function IndustriesSidebar({
  openSidebar,
  onOpenSidebar,
  onCloseSidebar,
}) {
  //----------------------------------------------------------------
  const [industries, setIndustries] = useState([]);
  const {
    data: industriesData,
  } = useFetcher(`/job/getAllJobIndustries`);

  useMemo(() => {
    return setIndustries(industriesData?.data || []);
  }, [industriesData?.data]);

  //----------------------------------------------------------------
  const [editing, setEditing] = useState(null);
  //----------------------------------------------------------------
  const [name, setName] = useState('');
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError();
    if (!name) {
      setError('Industry name is required');
      return;
    }
    setError();
    if (editing) {
      handleEdit(editing, { industry : name });
      return;
    }
    try {
      setLoading(true);
      const res = await JobService.addJobIndustry({ industry : name });
      setName('');
      setIndustries(prev => [...prev, res.data.data]);
    } catch (error) {
      setError('Failed to add new industry, try again later');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    try {
      setError(null);
      const res = await JobService.deleteJobIndustry(id);
      if (res.data.successMessage) {
        setIndustries(prev => prev.filter(c => c._id !== id));
      }
    } catch (error) {
      setError('Delete failed, try again later');
    }
  };

  const handleEdit = async (id, newIndustryName) => {
    try {
      setError(null);
      setLoading(true);
      const res = await JobService.updateJobIndustry(id, newIndustryName);
      if (res.data.successMessage) {
        setIndustries(prev =>
          prev.map(industry =>
            industry._id === res.data.data._id
              ? res.data.data
              : industry,
          ),
        );
      }
      setEditing(null);
    } catch (error) {
      setError('Edit industry failed, try again later');
    } finally {
      setLoading(false);
    }
  };

  useMemo(() => {
    if (editing) {
      setName(
        industries.filter(industry => industry._id === editing)[0]
          .industry,
      );
    } else {
      setName('');
    }
  }, [editing]);

  return (
    <>
      <Button
        onClick={onOpenSidebar}
        variant="outlined"
        color="secondary"
        size="small"
      >
        Industry
      </Button>

      <Drawer
        anchor="right"
        open={openSidebar}
        onClose={onCloseSidebar}
        PaperProps={{
          sx: { width: 320, border: 'none', overflow: 'hidden' },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1, py: 2 }}
        >
          <Typography variant="subtitle1" sx={{ ml: 1 }}>
            Industry
          </Typography>
          <IconButton onClick={onCloseSidebar}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>
        {error && (
          <Alert variant="standard" severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Divider />

        <Scrollbar>
          <Stack spacing={3} sx={{ px: 3 }}>
            <IndustriesList
              industries={industries}
              disabled={editing}
              onDelete={industryId => {
                handleDelete(industryId);
              }}
              onEdit={industryId => {
                if (editing) return;
                setEditing(industryId);
              }}
            />
            <form onSubmit={handleSubmit}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {!editing ? 'Add new' : 'Edit'} industry
              </Typography>
              <TextField
                label="Industry"
                color="secondary"
                value={name}
                onChange={e => setName(e.target.value)}
                fullWidth
                required
              />
              <Box sx={{ py: 2 }}>
                <Stack direction="row" spacing={2}>
                  {editing && !loading && (
                    <Button
                      color="error"
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={() => {
                        setError('');
                        setEditing(null);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                  {!loading ? (
                    <Button
                      fullWidth
                      size="large"
                      type="submit"
                      color="inherit"
                      variant="outlined"
                    >
                      {editing ? 'Update' : 'Submit'}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      size="large"
                      color="inherit"
                      variant="outlined"
                    >
                      <CircularProgress color="inherit" size={20} />
                      &nbsp; {editing ? 'Updating...' : 'Creating'}
                    </Button>
                  )}
                </Stack>
              </Box>
            </form>
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}